import { useStores } from '@littlebird/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { getEnvVar } from '@/env';
import { artifactPolicy, useArtifactContent } from '@/features/artifacts';
import { type HydratedFile } from '@/hooks/useHydratedFile';
import { useInViewport } from '@/hooks/useInViewport';
import { useTheme } from '@/providers/ThemeProvider';

import { requestSpriteThumbnail } from './sprite-thumbnail-generator';
import { type SpriteSummary } from './sprites.store';

export type SpriteThumbDisplay =
  | { kind: 'thumbnail'; src: string }
  | { kind: 'image'; content: SpriteImageContent }
  | { kind: 'icon' };

interface SpriteImageContent {
  text: string;
  url: string;
  versionKey: string;
}

const API_BASE = getEnvVar('VITE_API_URL');
const THUMBNAIL_BACKPRESSURE_DELAY_MS = 1_000;

export const useSpriteArtifact = (
  sprite: SpriteSummary,
  hydrated: HydratedFile
) => {
  const file = hydrated.file;
  const { ref, inViewport } = useInViewport<HTMLDivElement>();
  const { theme } = useTheme();
  const { files: filesStore } = useStores();
  const queryClient = useQueryClient();

  const renderer = artifactPolicy(file).renderer;
  const isImage = renderer === 'image';

  const { status, text, url, title, versionKey } = useArtifactContent(file, {
    surface: 'card',
    enabled: inViewport && isImage,
    canvasTitleProbe: inViewport,
  });

  const needsThumbnail =
    inViewport && renderer === 'canvas' && !sprite.thumbnail_url;

  useEffect(() => {
    if (!needsThumbnail || !file) return;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const requestThumbnail = () => {
      const accepted = requestSpriteThumbnail({
        file,
        queryClient,
        getFileUrl: filesStore.getFileUrl,
      });
      if (!accepted) {
        retryTimer = setTimeout(
          requestThumbnail,
          THUMBNAIL_BACKPRESSURE_DELAY_MS
        );
      }
    };
    requestThumbnail();
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [needsThumbnail, file, queryClient, filesStore]);

  const display = ((): SpriteThumbDisplay => {
    if (isImage)
      return status === 'ready'
        ? { kind: 'image', content: { text, url, versionKey } }
        : { kind: 'icon' };
    if (sprite.thumbnail_url && hydrated.status !== 'unavailable')
      return {
        kind: 'thumbnail',
        src: `${API_BASE}${sprite.thumbnail_url}/${theme}`,
      };
    return { kind: 'icon' };
  })();

  return { ref, title, display };
};

export type SpriteArtifact = ReturnType<typeof useSpriteArtifact>;
